import { produce, Draft } from 'immer';
import { BehaviorSubject } from 'rxjs';
import React from 'react';
import isEqual from 'lodash/isEqual';

export type BehaviorSubjectSelector<T, V> = (state: T) => V;
type Updater<T> = (arg: T | DraftFunction<T>) => void;
type DraftFunction<T> = (draft: Draft<T>) => void;

export function useBehaviorSubject<State, PartialValue = State>(
    subject: BehaviorSubject<State>,
    selector?: BehaviorSubjectSelector<State, PartialValue>,
): [PartialValue, Updater<State>] {
    const [, render] = React.useState(0);

    React.useEffect(() => {
        let cached = subject.value;

        const subscription = subject.subscribe((next) => {
            let equal = cached === next;

            if (!equal && selector) {
                equal = isEqual(selector(cached), selector(next));
            }

            if (!equal) {
                equal = isEqual(cached, next);
            }

            if (!equal) {
                render(Math.random());
            }

            cached = next;
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    return [
        selector ? selector(subject.value) : subject.value,
        (callback: any) => {
            subject.next(typeof callback === 'function' ? produce(subject.getValue(), callback) : callback);
        },
    ] as any;
}
